/**
 * Print an HTML document by injecting it into a hidden iframe and triggering
 * `contentWindow.print()`. Avoids the popup-blocker / `noopener` pitfalls of
 * `window.open(...).document.write(...)`.
 *
 * 必须等图片真的加载完再调 print()：打印预览是对当时的文档做一次快照，
 * 图还在路上就会被拍成破图 —— 附件图（发票/凭证）尤其明显，它要先走一趟
 * 我们的代理回源，比页面里其它资源慢得多。离屏渲染那条路（renderOffscreen，
 * PDF/签署用）早就这么等了，打印这条之前漏了，只等 load + 100ms。
 */
const IMAGE_WAIT_MS = 8000;

/** 等 iframe 里所有图片有结果（成功或失败都算），带上限，绝不无限等下去。 */
function waitForImages(win: Window, doc: Document): Promise<void> {
  const imgs = Array.from(doc.images);
  const pending = imgs.filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let left = pending.length;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const one = () => {
      left -= 1;
      if (left <= 0) finish();
    };
    for (const img of pending) {
      img.addEventListener('load', one, { once: true });
      // 失败也要放行：一张图取不到不该把整个打印卡死
      img.addEventListener('error', one, { once: true });
    }
    // 兜底：某些图片可能既不触发 load 也不触发 error（被扩展拦截等）
    win.setTimeout(finish, IMAGE_WAIT_MS);
  });
}

export function printHtmlViaIframe(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try {
      iframe.remove();
    } catch {
      /* noop */
    }
  };

  const triggerPrint = () => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    try {
      win.focus();
      win.addEventListener('afterprint', () => setTimeout(cleanup, 200), { once: true });
      win.print();
    } catch {
      cleanup();
      return;
    }
    // Safety net: if afterprint never fires (some browsers / cancelled dialog),
    // remove the iframe anyway after a long delay. 给足时间 —— 用户可能对着
    // 打印预览慢慢调页数/纸张，中途把 iframe 拆了预览会直接空掉。
    setTimeout(cleanup, 10 * 60_000);
  };

  iframe.addEventListener(
    'load',
    () => {
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument;
      if (!win || !doc) {
        cleanup();
        return;
      }
      // 先等图片有结果，再等字体，最后给一拍做版面收敛
      void waitForImages(win, doc)
        .then(() => (doc.fonts?.ready as Promise<unknown> | undefined) ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => {
          win.setTimeout(triggerPrint, 60);
        });
    },
    { once: true },
  );

  iframe.srcdoc = html;
  document.body.appendChild(iframe);
}
